/**
 * Раскладка комнаты заданного размера: где встанут её стены и какие из них уже есть.
 *
 * Комнату задают в чистоте, поэтому оси стен отодвинуты от чистого контура наружу на
 * полтолщины. Комната, придвинутая к существующей стене, «прилипает» к её грани: эта
 * сторона комнаты — сама существующая стена, вторую рядом не строим (иначе вышла бы
 * стена в стене или двойная стена с щелью). Ось такой стороны — ось существующей стены,
 * чистый размер — от её грани. Если существующая стена короче стороны комнаты, недостающий
 * кусок достраивается на той же оси той же толщины. Перпендикулярные стороны упираются в
 * существующую стену и режут её Т-стыком — как при черчении.
 *
 * Прилипают только к стенам, параллельным сторонам комнаты (комната всегда прямоугольник
 * по осям X и Y).
 */
import type { Pt } from "@/shared/lib/geometry";
import { MIN_WALL_LENGTH, type Wall, type WallGraph } from "@/shared/plan";

/** Стена, которую комната построит: отрезок оси и толщина. */
export interface RoomWallSegment {
	from: Pt;
	to: Pt;
	thickness: number;
}

export interface RoomLayout {
	/** Чистый контур по часовой стрелке от левого верхнего угла */
	inner: Pt[];
	/** Стены, которые нужно построить */
	segments: RoomWallSegment[];
	/** Сколько сторон комнаты — уже существующие стены */
	sharedSides: number;
}

/** Ближе этого, мм, грань соседней стены притягивает сторону комнаты. */
const STICK_DISTANCE = 300;
/** Стороны и стена должны перекрываться хотя бы на столько, мм, чтобы прилипнуть. */
const MIN_CONTACT = 100;
/** Допуск совпадения осей и граней, мм. */
const EPS = 1;

type Side = "top" | "right" | "bottom" | "left";

/** Стена, параллельная стороне комнаты: ось поперёк стороны и отрезок вдоль неё. */
interface ParallelWall {
	wall: Wall;
	/** Координата оси поперёк стороны: y у горизонтальной, x у вертикальной */
	axis: number;
	/** Отрезок вдоль стороны */
	min: number;
	max: number;
}

const parallelWalls = (graph: WallGraph, horizontal: boolean): ParallelWall[] => {
	const result: ParallelWall[] = [];
	for (const wall of Object.values(graph.walls)) {
		const a = graph.nodes[wall.a];
		const b = graph.nodes[wall.b];
		if (!a || !b) continue;
		const along = horizontal ? Math.abs(b.x - a.x) : Math.abs(b.y - a.y);
		const across = horizontal ? Math.abs(b.y - a.y) : Math.abs(b.x - a.x);
		if (along < MIN_WALL_LENGTH || across > EPS) continue;
		result.push({
			wall,
			axis: horizontal ? (a.y + b.y) / 2 : (a.x + b.x) / 2,
			min: horizontal ? Math.min(a.x, b.x) : Math.min(a.y, b.y),
			max: horizontal ? Math.max(a.x, b.x) : Math.max(a.y, b.y),
		});
	}
	return result;
};

/** Грань стены, обращённая к комнате с этой стороны. */
const faceToward = (side: Side, wall: ParallelWall) =>
	side === "top" || side === "left"
		? wall.axis + wall.wall.thickness / 2
		: wall.axis - wall.wall.thickness / 2;

/** Стена по эту сторону комнаты (а не по другую)? */
const onOuterSide = (side: Side, wall: ParallelWall, edge: number) =>
	side === "top" || side === "left" ? wall.axis <= edge + EPS : wall.axis >= edge - EPS;

/** Из двух сдвигов — меньший по модулю; null — сдвига нет. */
const nearest = (a: number | null, b: number | null) =>
	a === null ? b : b === null ? a : Math.abs(a) <= Math.abs(b) ? a : b;

export const roomLayout = (
	graph: WallGraph,
	center: Pt,
	width: number,
	depth: number,
	thickness: number,
): RoomLayout => {
	const horizontal = parallelWalls(graph, true);
	const vertical = parallelWalls(graph, false);
	const box = {
		top: center.y - depth / 2,
		bottom: center.y + depth / 2,
		left: center.x - width / 2,
		right: center.x + width / 2,
	};
	const span = (side: Side) =>
		side === "top" || side === "bottom" ? [box.left, box.right] : [box.top, box.bottom];
	const wallsFor = (side: Side) => (side === "top" || side === "bottom" ? horizontal : vertical);
	const overlap = (side: Side, wall: ParallelWall) => {
		const [lo, hi] = span(side);
		return Math.min(hi, wall.max) - Math.max(lo, wall.min);
	};

	/** Насколько сдвинуть сторону к грани ближайшей стены; null — некуда липнуть. */
	const stick = (side: Side): number | null => {
		let best: number | null = null;
		for (const wall of wallsFor(side)) {
			if (overlap(side, wall) < MIN_CONTACT) continue;
			if (!onOuterSide(side, wall, box[side])) continue;
			const shift = faceToward(side, wall) - box[side];
			const reach = Math.max(STICK_DISTANCE, wall.wall.thickness + thickness);
			if (Math.abs(shift) > reach) continue;
			if (best === null || Math.abs(shift) < Math.abs(best)) best = shift;
		}
		return best;
	};

	// По каждой оси липнем к ближайшей из двух граней
	const dy = nearest(stick("top"), stick("bottom")) ?? 0;
	const dx = nearest(stick("left"), stick("right")) ?? 0;
	box.top += dy;
	box.bottom += dy;
	box.left += dx;
	box.right += dx;

	/** Существующие стены, которыми стала сторона: их грань легла ровно на чистый контур. */
	const sharedWalls = (side: Side) =>
		wallsFor(side).filter(
			(wall) =>
				overlap(side, wall) >= MIN_CONTACT &&
				onOuterSide(side, wall, box[side]) &&
				Math.abs(faceToward(side, wall) - box[side]) < EPS,
		);
	const shared = {
		top: sharedWalls("top"),
		right: sharedWalls("right"),
		bottom: sharedWalls("bottom"),
		left: sharedWalls("left"),
	};
	/** Толщина стороны: у общей — толщина существующей стены */
	const sideThickness = (side: Side) => shared[side][0]?.wall.thickness ?? thickness;
	const axis = {
		top: box.top - sideThickness("top") / 2,
		bottom: box.bottom + sideThickness("bottom") / 2,
		left: box.left - sideThickness("left") / 2,
		right: box.right + sideThickness("right") / 2,
	};

	const segments: RoomWallSegment[] = [];
	/** Сторона от `lo` до `hi` вдоль оси; у общей — только куски, которых ещё нет. */
	const addSide = (side: Side, lo: number, hi: number, at: (t: number) => Pt) => {
		const covered = wallsFor(side)
			.filter((wall) => Math.abs(wall.axis - axis[side]) < EPS)
			.map((wall) => [wall.min, wall.max] as const)
			.toSorted((p, q) => p[0] - q[0]);
		let cursor = lo;
		const pieces: [number, number][] = [];
		for (const [min, max] of covered) {
			if (max <= cursor) continue;
			if (min >= hi) break;
			if (min > cursor) pieces.push([cursor, min]);
			cursor = Math.max(cursor, max);
		}
		if (cursor < hi) pieces.push([cursor, hi]);
		for (const [from, to] of pieces) {
			if (to - from < MIN_WALL_LENGTH) continue;
			segments.push({ from: at(from), to: at(to), thickness: sideThickness(side) });
		}
	};
	addSide("top", axis.left, axis.right, (x) => ({ x, y: axis.top }));
	addSide("right", axis.top, axis.bottom, (y) => ({ x: axis.right, y }));
	addSide("bottom", axis.left, axis.right, (x) => ({ x, y: axis.bottom }));
	addSide("left", axis.top, axis.bottom, (y) => ({ x: axis.left, y }));

	return {
		inner: [
			{ x: box.left, y: box.top },
			{ x: box.right, y: box.top },
			{ x: box.right, y: box.bottom },
			{ x: box.left, y: box.bottom },
		],
		segments,
		sharedSides: (["top", "right", "bottom", "left"] as const).filter(
			(side) => shared[side].length > 0,
		).length,
	};
};

/** Габарит комнаты по чистому контуру. */
export interface RoomBox {
	top: number;
	bottom: number;
	left: number;
	right: number;
}

/**
 * Выровнять начерченную комнату по линиям соседних стен: сторона, которая продолжает
 * параллельную стену (стена кончается у угла комнаты, а не идёт вдоль стороны), встаёт с ней
 * на одну ось — стены идут ровной линией без уступа в полстены. Комнату начертили от угла
 * готовой: привязка поставила точку на ось, и без выравнивания верх новой комнаты вышел бы
 * на полстены выше верха соседней. Начерченную мышью комнату каждая сторона ловит сама: и верх,
 * и низ встают в линию с соседней комнатой, хотя её высота не кратна шагу сетки. Размеры,
 * введённые с клавиатуры (`keepSize`), не меняются — комната только сдвигается целиком к
 * ближайшей линии. Стороны, вдоль которых стена идёт, не трогаем — их прилепит `roomLayout`.
 */
export const alignToWallLines = (
	graph: WallGraph,
	box: RoomBox,
	thickness: number,
	keepSize = false,
): RoomBox => {
	const horizontal = parallelWalls(graph, true);
	const vertical = parallelWalls(graph, false);
	/** Насколько сдвинуть комнату, чтобы сторона встала на ось соседней стены; null — некуда. */
	const align = (side: Side): number | null => {
		const walls = side === "top" || side === "bottom" ? horizontal : vertical;
		const [lo, hi] =
			side === "top" || side === "bottom" ? [box.left, box.right] : [box.top, box.bottom];
		/** Наружу от комнаты: ось стены этой стороны — на полтолщины за чистым контуром */
		const outward = side === "top" || side === "left" ? -1 : 1;
		const axis = box[side] + (outward * thickness) / 2;
		let best: ParallelWall | null = null;
		for (const wall of walls) {
			const overlap = Math.min(hi, wall.max) - Math.max(lo, wall.min);
			if (overlap >= MIN_CONTACT) continue;
			const reach = wall.wall.thickness + thickness;
			// Стена кончается у угла комнаты: зазор между ними не больше толщин
			const gap = Math.max(wall.min - hi, lo - wall.max);
			if (gap > reach) continue;
			// Ловит в пределах двух толщин: сторона мышью встаёт по сетке, а линия соседней
			// комнаты по сетке не лежит — ей чистый размер, а оси сдвинуты на полстены
			if (Math.abs(wall.axis - axis) > reach) continue;
			if (!best || Math.abs(wall.axis - axis) < Math.abs(best.axis - axis)) best = wall;
		}
		return best ? best.axis - axis : null;
	};
	const shift = {
		top: align("top"),
		bottom: align("bottom"),
		left: align("left"),
		right: align("right"),
	};
	if (keepSize) {
		// По каждой оси — к ближайшей из двух сторон, как и прилипание в `roomLayout`
		const dy = nearest(shift.top, shift.bottom) ?? 0;
		const dx = nearest(shift.left, shift.right) ?? 0;
		return {
			top: box.top + dy,
			bottom: box.bottom + dy,
			left: box.left + dx,
			right: box.right + dx,
		};
	}
	const next = {
		top: box.top + (shift.top ?? 0),
		bottom: box.bottom + (shift.bottom ?? 0),
		left: box.left + (shift.left ?? 0),
		right: box.right + (shift.right ?? 0),
	};
	// Две стороны не должны сойтись или вывернуться: такую пару не выравниваем
	if (next.bottom - next.top < MIN_WALL_LENGTH) {
		next.top = box.top;
		next.bottom = box.bottom;
	}
	if (next.right - next.left < MIN_WALL_LENGTH) {
		next.left = box.left;
		next.right = box.right;
	}
	return next;
};
